import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, ChevronDownIcon, SmartphoneIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
interface RaastRegisterProps {
  onBack: () => void;
  onHome: () => void;
}
type Step = 'empty' | 'form' | 'review' | 'otp' | 'success';
export function RaastRegister({
  onBack,
  onHome
}: RaastRegisterProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('empty');
  const [direction, setDirection] = useState(1);
  // Form State
  const [mobileNumber, setMobileNumber] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`raast-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`raast-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2388">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2389">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2390" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2391">{title}</h1>
      <button onClick={onHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2392">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2393" />
      </button>
    </div>;
  const renderEmpty = () => <motion.div key="empty" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2394">
      {renderHeader('Register RAAST ID', onBack)}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8 text-center" data-id="element-2395">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2396">
          <SmartphoneIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-2397" />
        </div>
        <h2 className={`text-xl font-bold mb-3 ${textPri}`} data-id="element-2398">
          No RAAST ID Found
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-2399">
          You haven't registered a RAAST ID yet. Link your mobile number to your
          account to receive payments instantly.
        </p>
        <button onClick={() => navigateTo('form')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2400">
          Register Now
        </button>
      </div>
    </motion.div>;
  const renderForm = () => <motion.div key="form" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2401">
      {renderHeader('Register RAAST ID', () => navigateTo('empty', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2402">
        <div className="space-y-6 mb-auto" data-id="element-2403">
          <div data-id="element-2404">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2405">
              Account Title
            </label>
            <input type="text" value="KASHISH AHMED" disabled className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border opacity-70 ${inputBg} ${textPri} ${borderClr}`} data-id="element-2406" />
          </div>
          <div data-id="element-2407">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2408">
              Mobile Number / RAAST ID
            </label>
            <input type="tel" value={mobileNumber} onChange={e => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="03XX XXXXXXX" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2409" />
          </div>
          <div className="relative" data-id="element-2410">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2411">
              Link to Account
            </label>
            <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between border ${cardBg} ${borderClr}`} data-id="element-2412">
              <div className="text-left" data-id="element-2413">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2414">
                  {selectedAccount.label}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2415">
                  {selectedAccount.number}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2416" />
            </button>
            <AnimatePresence data-id="element-2417">
              {showAccountDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-full left-0 right-0 mt-2 rounded-2xl shadow-xl overflow-hidden border z-10 ${cardBg} ${borderClr}`} data-id="element-2418">
                  {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                setSelectedAccount(acc);
                setShowAccountDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${borderClr} ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2419">
                      <p className={`font-bold ${textPri}`} data-id="element-2420">{acc.label}</p>
                      <p className={`text-sm ${textSec}`} data-id="element-2421">{acc.number}</p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
        <button onClick={() => navigateTo('review')} disabled={mobileNumber.length !== 11} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2422">
          Next
        </button>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2423">
      {renderHeader('Review Details', () => navigateTo('form', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2424">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2425">
          <h3 className={`text-[16px] font-bold mb-6 text-center ${textPri}`} data-id="element-2426">
            Confirm Registration
          </h3>
          <div className="space-y-4" data-id="element-2427">
            <div className="flex justify-between" data-id="element-2428">
              <span className={textSec} data-id="element-2429">Account Title</span>
              <span className={`font-bold ${textPri}`} data-id="element-2430">KASHISH AHMED</span>
            </div>
            <div className="flex justify-between" data-id="element-2431">
              <span className={textSec} data-id="element-2432">RAAST ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2433">{mobileNumber}</span>
            </div>
            <div className="flex justify-between" data-id="element-2434">
              <span className={textSec} data-id="element-2435">Linked Account</span>
              <span className={`font-bold ${textPri}`} data-id="element-2436">
                {selectedAccount.number.slice(-4)}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2437">
          Confirm
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2438">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2439">
        <div className="mb-8 text-center" data-id="element-2440">
          <p className={`text-[14px] ${textSec}`} data-id="element-2441">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2442">
          {otp.map((digit, i) => <input key={i} id={`raast-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2443" />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2444">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2445">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2446">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2447">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden" data-id="element-2448">
        {/* Confetti simulation */}
        {Array.from({
        length: 20
      }).map((_, i) => <motion.div key={i} initial={{
        top: '-10%',
        left: `${Math.random() * 100}%`,
        opacity: 1
      }} animate={{
        top: '110%',
        rotate: Math.random() * 360
      }} transition={{
        duration: 2 + Math.random() * 2,
        repeat: Infinity,
        delay: Math.random() * 2
      }} className={`absolute w-3 h-3 rounded-sm ${['bg-[#F37024]', 'bg-[#22C55E]', 'bg-[#3B82F6]'][i % 3]}`} data-id="element-2449" />)}

        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15
      }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30 z-10" data-id="element-2450">
          <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-2451" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-2xl font-bold mb-2 text-center z-10 ${textPri}`} data-id="element-2452">
          Registration Successful!
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-[15px] text-center mb-8 z-10 ${textSec}`} data-id="element-2453">
          Your RAAST ID has been created and linked successfully.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`w-full rounded-[24px] p-6 shadow-sm border z-10 ${cardBg} ${borderClr}`} data-id="element-2454">
          <div className="space-y-4" data-id="element-2455">
            <div className="flex justify-between" data-id="element-2456">
              <span className={textSec} data-id="element-2457">RAAST ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2458">{mobileNumber}</span>
            </div>
            <div className="flex justify-between" data-id="element-2459">
              <span className={textSec} data-id="element-2460">Linked Account</span>
              <span className={`font-bold ${textPri}`} data-id="element-2461">
                {selectedAccount.number.slice(-4)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2462">
              <span className={textSec} data-id="element-2463">Status</span>
              <span className="font-bold text-[#22C55E]" data-id="element-2464">Active</span>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="px-6 pb-8 z-10" data-id="element-2465">
        <button onClick={onHome} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2466">
          Done
        </button>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2467">
      {step === 'empty' && renderEmpty()}
      {step === 'form' && renderForm()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}